import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
} from '@mui/material';
import api from '../api/axios';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (slug: string) => void;
}

export default function CreateEntityTypeDialog({ open, onClose, onCreated }: Props) {
  const [label, setLabel] = useState('');
  const [shortName, setShortName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLabelChange = (value: string) => {
    setLabel(value);
    // Auto-generate slug from label if slug hasn't been manually edited
    if (!slug || slug === labelToSlug(label)) {
      setSlug(labelToSlug(value));
    }
    // Auto-set short_name from label if it hasn't been manually edited
    if (!shortName || shortName === label) {
      setShortName(value);
    }
  };

  const labelToSlug = (val: string) =>
    val.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

  const handleSave = async () => {
    if (!slug.trim() || !label.trim() || !shortName.trim()) {
      setError('Label, short name, and slug are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post('/schemes/prompts/entity-types/', {
        slug,
        label,
        short_name: shortName,
        description,
        sort_order: 999,
        is_visible: true,
      });
      handleClose();
      onCreated(slug);
    } catch (err: unknown) {
      const e = err as { response?: { data?: Record<string, string[]> } };
      const data = e.response?.data;
      if (data) {
        const msg = Object.values(data).flat().join('; ');
        setError(msg || 'Failed to create entity type');
      } else {
        setError('Failed to create entity type');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setLabel('');
    setShortName('');
    setSlug('');
    setDescription('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Entity Type</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            fullWidth
            label="Label"
            value={label}
            onChange={e => handleLabelChange(e.target.value)}
            placeholder="e.g., Defrauded Parties"
            helperText="Full display name"
          />
          <TextField
            fullWidth
            label="Short Name"
            value={shortName}
            onChange={e => setShortName(e.target.value)}
            placeholder="e.g., Defrauded Parties"
            helperText="Short display name for sidebar"
          />
          <TextField
            fullWidth
            label="Slug"
            value={slug}
            onChange={e => setSlug(e.target.value)}
            placeholder="e.g., defrauded_parties"
            helperText="Unique identifier (snake_case). Cannot be changed after creation."
            sx={{ '& .MuiInputBase-root': { fontFamily: 'monospace' } }}
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="What does this entity type extract?"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !slug.trim() || !label.trim() || !shortName.trim()}
        >
          {saving ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
